import { useNavigate } from 'react-router';
import { MonitorOff, MonitorUp, PhoneOff, Signal, Video, VideoOff } from 'lucide-react';
import { Permission } from '@spawnwire/shared';
import { clsx, IconButton, Spinner } from '../../components/ui';
import { can, useChannelPermissions } from '../../lib/perms';
import { channelPath } from '../../lib/nav';
import { leaveVoice, toggleCamera, toggleScreenShare, useVoice } from '../../lib/voice';
import { privateChannelName, useStore } from '../../store/store';

export function VoicePanel() {
  const navigate = useNavigate();
  const channelId = useVoice((v) => v.channelId);
  const status = useVoice((v) => v.status);
  const video = useVoice((v) => v.video);
  const screen = useVoice((v) => v.screen);
  const remote = useVoice((v) => v.remote);
  const channel = useStore((s) => (channelId ? s.channels[channelId] : undefined));
  const spaceName = useStore((s) => (channel?.spaceId ? s.spaces[channel.spaceId]?.name : null));
  const connection = useStore((s) => s.connection);
  const perms = useChannelPermissions(channelId);
  if (!channelId || !channel) return null;

  const states = Object.values(remote).map((r) => r.connection);
  const relayFailed = Object.values(remote).some((r) => r.relayFailed);
  const degraded = connection !== 'ready' || relayFailed || states.some((s) => s === 'disconnected' || s === 'failed');
  const connecting = status === 'connecting' || states.some((s) => s === 'new' || s === 'connecting');
  const label = connection !== 'ready' ? 'Reconnecting' : relayFailed ? 'Voice relay unreachable' : degraded ? 'Connection unstable' : connecting ? 'Connecting' : 'Voice connected';
  const name = channel.spaceId ? channel.name : privateChannelName(useStore.getState(), channel);
  const canStream = can(perms, Permission.STREAM);

  return (
    <div className="shrink-0 border-b border-line px-2 py-2">
      <div className="flex items-center gap-2">
        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => navigate(channelPath(channel))}>
          <span className={clsx('flex items-center gap-1.5 text-sm font-semibold', degraded ? 'text-warning' : connecting ? 'text-muted' : 'text-success')}>
            {connecting && !degraded ? <Spinner className="size-3.5" /> : <Signal className="size-4" aria-hidden />}
            {label}
          </span>
          <span className="block truncate text-xs text-muted hover:underline">
            {name}
            {spaceName ? ` / ${spaceName}` : ''}
          </span>
        </button>
        <IconButton icon={PhoneOff} label="Disconnect" onClick={() => void leaveVoice()} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={!canStream}
          onClick={() => void toggleCamera()}
          className={clsx('flex h-8 items-center justify-center gap-1.5 rounded-md text-sm', video ? 'bg-accent text-accent-fg' : 'bg-active text-fg hover:bg-line-strong', 'disabled:opacity-40')}
          aria-pressed={video}
        >
          {video ? <Video className="size-4" /> : <VideoOff className="size-4" />} Camera
        </button>
        <button
          type="button"
          disabled={!canStream}
          onClick={() => void toggleScreenShare()}
          className={clsx('flex h-8 items-center justify-center gap-1.5 rounded-md text-sm', screen ? 'bg-accent text-accent-fg' : 'bg-active text-fg hover:bg-line-strong', 'disabled:opacity-40')}
          aria-pressed={screen}
        >
          {screen ? <MonitorOff className="size-4" /> : <MonitorUp className="size-4" />} Screen
        </button>
      </div>
    </div>
  );
}
